import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import http from "@/lib/api";
import Lightbox from "@/components/Lightbox";
import SmartImage from "@/components/SmartImage";
import { RevealSection, DrawnLine, Marquee, StaggerGroup, staggerItem } from "@/components/Reveal";

// Painter's portrait from the original e-monsite site
const HERO_PORTRAIT = "https://thiebaud-patrick.e-monsite.com/medias/images/74312061-2636046213101054-8009735577471549440-o.jpg";
// Beautiful painting close-up for hero (artistic, brushstrokes, oil paint texture)
const HERO_BG = "/hero-bg.webp";
const PAINT_TEXTURE = "https://images.unsplash.com/photo-1547333590-47fae5f58d21?q=80&w=2000";
const RENCONTRE_IMG = "/rencontre.webp";

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [active, setActive] = useState(null);
    const heroRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
    const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

    useEffect(() => {
        http.get("/artworks", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 4)));
        http.get("/testimonials").then(({ data }) => setTestimonials(data.slice(0, 3)));
    }, []);

    return (
        <main data-testid="home-page" className="bg-[var(--bg)] text-[var(--ink)]">
            {/* ========== HERO ========== */}
            <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
                {/* Painting texture background */}
                <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
                    <img
                        src={HERO_BG}
                        alt=""
                        className="w-full h-full object-cover hero-portrait"
                        onError={(e) => { e.target.src = PAINT_TEXTURE; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)]/70 to-[var(--bg)]/30" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg)]/80 via-transparent to-[var(--bg)]/40" />
                </motion.div>

                {/* Floating portrait of the painter, top-right */}
                <motion.div
                    initial={{ opacity: 0, x: 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute top-32 right-6 md:right-12 lg:right-20 z-10 hidden md:block"
                    style={{ y: useTransform(scrollYProgress, [0, 1], [0, -80]) }}
                >
                    <div className="relative w-48 lg:w-64 aspect-[3/4]">
                        <div className="absolute -inset-3 border border-gilt opacity-60" />
                        <img
                            src={HERO_PORTRAIT}
                            alt="Patrick Mathurin"
                            className="relative w-full h-full object-cover hero-portrait"
                        />
                        <div className="absolute -bottom-8 left-0 right-0 text-center">
                            <div className="overline text-gilt text-[9px]">L'artiste</div>
                            <div className="serif italic-serif text-lg">Patrick Mathurin</div>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    style={{ opacity: heroOpacity }}
                    className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-3xl"
                    >
                        <div className="overline mb-6" data-testid="hero-overline">
                            Atelier de Patrick Mathurin · depuis 2013
                        </div>
                        <h1 className="serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] mb-8">
                            La peinture<br />
                            comme une <span className="italic-serif text-gilt">méditation</span>.
                        </h1>
                        <p className="text-lg md:text-xl text-bone-200 max-w-xl leading-relaxed mb-10 font-light">
                            Portraits à l'huile sur lin, grands formats acryliques d'inspiration asiatique.
                            Une œuvre intime, classique, faite dans le silence et la lumière.
                        </p>
                        <div className="flex flex-wrap items-center gap-6">
                            <Link to="/galerie" className="btn-gilt" data-testid="hero-cta-gallery">
                                Découvrir la galerie <ArrowRight size={14} strokeWidth={1.5} />
                            </Link>
                        </div>
                    </motion.div>

                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-bone-300">
                        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                        <ArrowDown size={14} className="animate-bounce" />
                    </div>
                </motion.div>
            </section>

            {/* ========== MARQUEE BAND ========== */}
            <div className="border-y border-[var(--border)] py-8 bg-[var(--bg-elev)]">
                <Marquee>Portrait · Huile · Acrylique · Méditation · Asiatique · </Marquee>
            </div>

            {/* ========== INTRO MANIFESTO ========== */}
            <RevealSection className="py-24 md:py-40">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <div className="overline mb-6">Manifeste</div>
                        <h2 className="serif text-3xl md:text-4xl leading-tight">
                            Autodidacte à <span className="italic-serif text-gilt">100 %</span>,<br />
                            classique d'instinct.
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:col-start-6 space-y-6 text-lg leading-relaxed text-bone-200 font-light">
                        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}>
                            J'ai découvert la peinture en 2013, par hasard, sans aucune formation préalable.
                            <span className="text-bone-50"> 70 % de mon travail est consacré au portrait</span> sur toile de lin ou de coton —
                            des visages, peints d'après photo, qui tentent de saisir une présence.
                        </motion.p>
                        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.25 }}>
                            Les 30 % restants sont pour les <span className="text-bone-50">paysages, animaux et
                            compositions d'inspiration asiatique</span>, fascinantes par leur philosophie et leur silence.
                        </motion.p>
                        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.4 }} className="italic-serif text-2xl text-bone-100 pt-4">
                            « Il n'y a rien de plus apaisant que de peindre. La toile m'évade,
                            comme une méditation. »
                        </motion.p>
                    </div>
                </div>
            </RevealSection>

            <DrawnLine className="max-w-7xl mx-auto" />

            {/* ========== FEATURED WORKS ========== */}
            <RevealSection className="py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-end justify-between mb-16">
                        <div>
                            <div className="overline mb-4">Sélection</div>
                            <h2 className="serif text-4xl md:text-5xl">Œuvres signatures</h2>
                        </div>
                        <Link to="/galerie" className="hidden md:inline-flex link-underline text-sm tracking-[0.18em] uppercase text-bone-200" data-testid="featured-see-all">
                            Voir tout
                        </Link>
                    </div>

                    <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {featured.map((a, i) => (
                            <motion.button
                                key={a.id}
                                variants={staggerItem}
                                data-testid={`featured-tile-${i}`}
                                onClick={() => setActive(a)}
                                className="art-tile group block text-left"
                            >
                                <div className={`overflow-hidden bg-ink-800 ${i % 2 === 0 ? "aspect-[3/4]" : "aspect-[4/5]"}`}>
                                    <SmartImage src={a.image_url} videoUrl={a.video_url} alt={a.title} className="w-full h-full object-cover" fallback={a.title} />
                                </div>
                                <div className="mt-4">
                                    <div className="serif text-xl group-hover:text-gilt transition-colors">{a.title}</div>
                                    <div className="text-xs text-bone-400 uppercase tracking-[0.2em] mt-1">{a.category}</div>
                                </div>
                            </motion.button>
                        ))}
                    </StaggerGroup>
                </div>
            </RevealSection>

            <DrawnLine className="max-w-7xl mx-auto" />

            {/* ========== SPLIT — about teaser ========== */}
            <RevealSection className="py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, x: -30 }}
                        whileInView={{ opacity: 1, scale: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div className="flex justify-end mb-6">
                            <div className="border border-gilt bg-[var(--bg)] p-6 text-center">
                                <div className="serif text-5xl text-gilt">12+</div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-300 mt-1">Années d'atelier</div>
                            </div>
                        </div>
                        <div className="aspect-[4/5] overflow-hidden bg-white">
                            <img src={RENCONTRE_IMG} alt="Rencontre avec l'artiste" className="w-full h-full object-contain" />
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="overline mb-6">Rencontre</div>
                        <h2 className="serif text-4xl md:text-5xl leading-tight mb-8">
                            Une peinture<br />faite dans le <span className="italic-serif text-gilt">silence</span>.
                        </h2>
                        <p className="text-bone-200 leading-relaxed mb-6 font-light text-lg">
                            Patrick Mathurin (Patrick Thiebaud) peint depuis 2013. Sans école, sans maître,
                            il a forgé un style classique habité d'une sensibilité contemporaine et
                            d'une fascination assumée pour l'esthétique asiatique.
                        </p>
                        <p className="text-bone-200 leading-relaxed mb-10 font-light text-lg">
                            Créateur et gestionnaire du groupe privé « Artistes Peintres Internationaux Réunis »,
                            il fédère aujourd'hui 616 membres dans 47 pays.
                        </p>
                        <Link to="/a-propos" className="btn-gilt" data-testid="home-about-cta">
                            Découvrir l'artiste <ArrowRight size={14} strokeWidth={1.5} />
                        </Link>
                    </motion.div>
                </div>
            </RevealSection>

            {/* ========== TESTIMONIALS strip ========== */}
            {testimonials.length > 0 && (
                <>
                    <DrawnLine className="max-w-7xl mx-auto" />
                    <RevealSection className="py-24 md:py-32">
                        <div className="max-w-7xl mx-auto px-6 md:px-12">
                            <div className="text-center mb-16">
                                <div className="overline mb-4">Livre d'or</div>
                                <h2 className="serif text-4xl md:text-5xl">Ce qu'ils en disent</h2>
                            </div>
                            <StaggerGroup className="grid md:grid-cols-3 gap-10 md:gap-14" stagger={0.15}>
                                {testimonials.map((t, i) => (
                                    <motion.figure key={t.id} variants={staggerItem} data-testid={`home-testimonial-${i}`} className="border-l border-gilt pl-6">
                                        <blockquote className="italic-serif text-xl leading-relaxed text-bone-100 mb-4">
                                            « {t.message} »
                                        </blockquote>
                                        <figcaption className="overline text-bone-300">
                                            {t.name}{t.location ? ` · ${t.location}` : ""}
                                        </figcaption>
                                    </motion.figure>
                                ))}
                            </StaggerGroup>
                            <div className="text-center mt-16">
                                <Link to="/livre-d-or" className="btn-ghost" data-testid="home-livre-or-cta">
                                    Tout le livre d'or <ArrowRight size={12} />
                                </Link>
                            </div>
                        </div>
                    </RevealSection>
                </>
            )}

            {active && <Lightbox artwork={active} onClose={() => setActive(null)} />}
        </main>
    );
}
